import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Message, MessageService } from 'primeng/api';
import { Observable, Subject, forkJoin, take, takeUntil } from 'rxjs';
import { AuthService } from 'src/app/auth/auth.service';
import { AdminService } from 'src/app/services/admin.service';
import { AgencyService } from 'src/app/services/agency.service';
import { LoginService } from 'src/app/services/login.service';
import { LookupService } from 'src/app/services/lookup.service';
import { AppConstants } from 'src/app/shared/app.constants';
import { AuthRoles, AuthUserType } from '../../../auth/auth.types';
import { AccountComponent } from '../../account.component';

@Component({
  selector: 'ness-userinformation',
  templateUrl: './userinformation.component.html'
})
export class UserinformationComponent implements OnInit {
  @Input() ownProfile: boolean = true;
  @Input() userInfoForOtherUser: ServerModels.UserInfoViewModel = null;
  @Output() userUpdated = new EventEmitter<boolean>();
  @Output() onChange = new EventEmitter<{ originalEvent: MouseEvent, index: number }>();

  fieldDivisions$: Observable<ServerModels.ENESS_REF_FieldDivisions[]> = this.loginService.fieldDivisions$;
  filteredAgencies: ServerModels.ENESS_REF_Agency[] = [];
  filteredRoles: ServerModels.ENESS_ApplicationRole[] = [];
  filteredJobTitles: ServerModels.ENESS_REF_JobTitles[] = [];
  filteredFDGroup: ServerModels.ENESS_REF_IntelligenceReportATFReferralGroups[] = [];
  showUserCommentsForm: boolean = false;
  showATFHelpText: boolean = false;
  showStateLocalHelpText: boolean = false;
  userProfileForm: FormGroup = this.fb.group({
    FirstName: ['', Validators.required],
    LastName: ['', Validators.required],
    Email: [{ value: '', disabled: true }, Validators.required],
    PhoneNumber: '',
    Roles: this.fb.array([]),
    UserTypeID: ['', Validators.required],
    JobTitleID: [{ value: '', disabled: true }, Validators.required],
    OtherJobTitle: '',
    AgencyID: { value: '', disabled: true },
    StateID: '',
    FieldDivisionID: '',
    FieldDivisionGroupID: [{ value: '', disabled: true }]
  });
  userInfo: ServerModels.UserInfoViewModel = null;
  userTypes: ServerModels.ENESS_REF_UserType[] = [];
  jobTitles: ServerModels.ENESS_REF_JobTitles[] = [];
  fdGroups: ServerModels.ENESS_REF_IntelligenceReportATFReferralGroups[] = [];
  viewMode: boolean = true;
  userHistoryForm: FormGroup;
  textAreaRows: number = AppConstants.defaultTextAreaRows;
  isFieldDivisionLeadership: boolean = false;

  private _cdsNonEditableFormControls: string[] = ['UserTypeID', 'StateID', 'AgencyID'];
  private _formData: ServerModels.ENESS_XREF_UserProfileChangeRequests;
  private _roles: ServerModels.ENESS_ApplicationRole[] = [];
  private _showAgency: boolean;
  private _showFieldDivision: boolean;
  private _showOtherJobTitle: boolean = false;
  private _unsubscribe$: Subject<void> = new Subject<void>();

  constructor(private fb: FormBuilder,
    private loginService: LoginService,
    private authService: AuthService,
    public lookupService: LookupService,
    private agencyService: AgencyService,
    private adminService: AdminService,
    private messageService: MessageService,
    private route: ActivatedRoute) { }

  get roleName(): string {
    if (!this.userInfo || !this.userInfo.Roles.length)
      return '';

    if (this.userInfoForOtherUser == null) {
      return this.userInfo.Roles.filter(r => r.Name !== 'NESS Administrator' && r.Name !== 'CDS Approver')[0]?.Name || '';
    }
    else {
      if (!this.userInfoForOtherUser.Roles.length) {
        return '';
      } else {
        const nonprivilagedRoles = this.userInfoForOtherUser.Roles.filter(r => r.Name !== 'NESS Administrator' && r.Name !== 'CDS Approver');

        if (!nonprivilagedRoles.length) {
          return '';
        }

        return nonprivilagedRoles[0].Name;
      }
    }
  }

  get roles(): ServerModels.ENESS_ApplicationRole[] {
    return this._roles;
  }

  get showAgency(): boolean {
    return this._showAgency;
  }

  get showFieldDivision(): boolean {
    return this._showFieldDivision;
  }

  get showOtherJobTitle(): boolean {
    return this._showOtherJobTitle;
  }

  get Roles(): FormArray {
    return this.userProfileForm.get('Roles') as FormArray;
  }

  get UserJobTitle(): string {
    let jobTitle = '';
    if (this.ownProfile && !!this.userInfo?.JobTitleName) {
      jobTitle = `${this.userInfo.JobTitleName}${this.userInfo.OtherJobTitle ? ': ' + this.userInfo.OtherJobTitle : ' (' + this.userInfo.JobTitleAbbreviation + ')'}`;
    } else if (!!this.userInfoForOtherUser?.JobTitleName) {
      jobTitle = `${this.userInfoForOtherUser.JobTitleName}${this.userInfoForOtherUser.OtherJobTitle ? ': ' + this.userInfoForOtherUser.OtherJobTitle : ' (' + this.userInfoForOtherUser.JobTitleAbbreviation + ')'}`;
    }

    return jobTitle;
  }

  ngOnInit() {
    forkJoin({
      userInfo: this.loginService.getUserInfo(),
      roles: this.loginService.getAllRoles(),
      userType: this.loginService.getUserTypes(),
      jobTitle: this.loginService.getJobTitles(),
      group: this.loginService.getAllFieldDivisionGroups()
    }).pipe(takeUntil(this._unsubscribe$)).subscribe({
      next: (val: { userInfo: ServerModels.UserInfoViewModel, roles: ServerModels.ENESS_ApplicationRole[], userType: ServerModels.ENESS_REF_UserType[], jobTitle: ServerModels.ENESS_REF_JobTitles[], group: ServerModels.ENESS_REF_IntelligenceReportATFReferralGroups[] }) => {
        this.userInfo = val.userInfo;
        this._roles = val.roles;
        this.userTypes = val.userType;
        this.jobTitles = val.jobTitle;
        this.fdGroups = val.group;

        if (this.userInfoForOtherUser) {
          this.setProfileInfo(this.userInfoForOtherUser);
        }
        else if (this.userInfo) {
          this.setProfileInfo(this.userInfo);
        }
      }
    });
    this.userHistoryForm = this.fb.group({
      Comments: ['', Validators.required]
    });

  }

  ngOnDestroy(): void {
    this._unsubscribe$.next();
    this._unsubscribe$.complete();
    this.messageService.clear();
  }

  public isCDSApprover(): boolean {
    if (this.ownProfile) {
      return this.authService.isCDSApprover();
    }

    if (this.userInfoForOtherUser) {
      return this.userInfoForOtherUser.Roles.some((r: ServerModels.RoleViewModel) => r.Name === 'CDS Approver');
    }

    return false;
  }

  public isNESSAdmin(): boolean {
    return this.authService.hasRole(['NESS Administrator']);
  }

  public hasUserTypeRole(userTypeRoles: { roles: AuthRoles[], userType: AuthUserType }): boolean {
    if (this.ownProfile) {
      return this.authService.hasUserTypeRole([userTypeRoles]);
    }
    return false;
  }

  private setProfileInfo(user: ServerModels.UserInfoViewModel): void {
    this._showFieldDivision = user.UserTypeName === 'ATFFieldDivision';
    this.updateFieldDivision(this.showFieldDivision);
    this.updateAgency(user.UserTypeName === 'StateLocalLE');
    this.updateJobTitles(user.UserTypeID);
    this.updateFDGroups(user.FieldDivisionID);

    this.showATFHelpText = (user.UserTypeName === 'ATFFieldDivision' || user.UserTypeName === 'ATFHQ');
    this.showStateLocalHelpText = user.UserTypeName === 'StateLocalLE';

    let roles = [];
    if (user.Roles && user.Roles.length > 0) {
      user.Roles.forEach(r => {
        if (r.Name !== 'NESS Administrator' && r.Name !== 'CDS Approver') {
          roles.push(this.fb.group({ Id: [r.Id, Validators.required] }));
        }
      });

      // Set role dropdown default value if no role returned for current user
      if (!roles.length) {
        roles.push(this.fb.group({ Id: ['', Validators.required] }));
      }
    }
    else {
      roles.push(this.fb.group({ Id: ['', Validators.required] }));
    }
    this.userProfileForm.setControl('Roles', this.fb.array(roles));

    if (user.JobTitleName === 'Other') {
      this._showOtherJobTitle = true;
    }

    this._patchUserProfileForm(user);

    if (this.isCDSApprover() && this.isNESSAdmin()) {
      this._disableFormControls(this._cdsNonEditableFormControls);
    }
  }

  private _disableFormControls(controlNames: string[]): void {
    for (let i = 0; i < controlNames.length; i++) {
      this.userProfileForm.get(controlNames[i]).disable();
    }
  }

  private _patchUserProfileForm(user: ServerModels.UserInfoViewModel) {
    this._showOtherJobTitle = !!user.OtherJobTitle;
    this.userProfileForm.patchValue({
      FirstName: user.FirstName,
      LastName: user.LastName,
      Email: user.Email,
      PhoneNumber: user.PhoneNumber,
      UserTypeID: user.UserTypeID?.toUpperCase() || '',
      JobTitleID: user.JobTitleID?.toLowerCase() || '',
      OtherJobTitle: user.OtherJobTitle || '',
      AgencyID: user.AgencyID?.toUpperCase() || '',
      StateID: user.AgencyStateID?.toUpperCase() || '',
      FieldDivisionID: user.FieldDivisionID?.toUpperCase(),
      FieldDivisionGroupID: user.FieldDivisionGroupID?.toLowerCase() || '',
    });
  }

  public toggleEditMode(): void {
    this.viewMode = !this.viewMode;
    if (this.ownProfile) {
      this.resetData(this.userInfo);
    }
    else {
      this.resetData(this.userInfoForOtherUser);
    }
  }

  public onCancel(): void {
    this.viewMode = true;
    if (this.ownProfile) {
      this.resetData(this.userInfo);
    }
    else {
      this.resetData(this.userInfoForOtherUser);
    }
  }

  public resetData(user: ServerModels.UserInfoViewModel): void {
    if (!user) {
      return;
    }

    this._showFieldDivision = user.UserTypeName === 'ATFFieldDivision';
    this._showAgency = user.UserTypeName === 'StateLocalLE';
    this.updateJobTitles(user.UserTypeID);
    this.updateFDGroups(user.FieldDivisionID);
    this.filteredRoles = this._showAgency ? [...this.roles.filter(r => r.Name !== 'Power User')] : [...this.roles];

    if (this._showAgency) {
      this.updateAgenciesCtrl(user.AgencyStateID, user.AgencyID);
    }

    let selectJobTitle = this.jobTitles.find(j => j.ID.toLowerCase() == user.JobTitleID?.toLowerCase());
    if (selectJobTitle && selectJobTitle.JobTitle === 'Other') {
      this._showOtherJobTitle = true;
    }

    this.setProfileInfo(user);
  }

  public onSelectState(stateId: string): void {
    this.updateAgenciesCtrl(stateId);
  }

  public onSelectUserType(userTypeId: string): void {
    const selectedUserType: ServerModels.ENESS_REF_UserType = this.userTypes.find(ut => ut.Id.toLowerCase() === userTypeId.toLowerCase());
    this.updateFieldDivision(selectedUserType && selectedUserType.UserTypeName === 'ATFFieldDivision');
    this.updateAgency(selectedUserType && selectedUserType.UserTypeName === 'StateLocalLE');
    const canSelectPowerUser = selectedUserType && selectedUserType.UserTypeName !== 'StateLocalLE';
    this.updateRoles(canSelectPowerUser);
    this.updateJobTitles(userTypeId);
    this.showATFHelpText = selectedUserType && (selectedUserType.UserTypeName === 'ATFFieldDivision' || selectedUserType.UserTypeName === 'ATFHQ');
    this.showStateLocalHelpText = selectedUserType && selectedUserType.UserTypeName === 'StateLocalLE';
  }

  public onSelectJobTitle(jobTitleId: string) {
    const otherJobTitleCtrl: FormControl = <FormControl>this.userProfileForm.get('OtherJobTitle');
    const selectedJobTitle: ServerModels.ENESS_REF_JobTitles = this.jobTitles.find(x => x.ID.toLowerCase() === jobTitleId.toLowerCase());
    this._showOtherJobTitle = selectedJobTitle.JobTitle.toLowerCase() === 'other';

    if (this._showOtherJobTitle) {
      otherJobTitleCtrl.setValidators(Validators.required);
    } else {
      otherJobTitleCtrl.clearValidators();
    }

    otherJobTitleCtrl.reset('');
  }

  public onSelectFieldDivision(fieldDivisionId: string) {
    this.updateFDGroups(fieldDivisionId);
  }

  public onSubmit(): void {
    if (this.userProfileForm.value != null && this.userProfileForm.valid) {
      this._formData = Object.assign({}, this.userProfileForm.getRawValue());
      this._formData.RoleID = (<FormArray>this.userProfileForm.get('Roles')).at(0).get('Id').value;
      this._formData.UserName = this.userInfo.Email;
      this._formData.FieldDivisionID = this._formData.FieldDivisionID || null;
      this._formData.AgencyID = this._formData.AgencyID || null;
      if (this.ownProfile) {
        let errorMessage = 'An error occurred while updating the user profile. The user profile changes were not saved.';
        this.loginService.addUpdateUserProfile(this._formData).subscribe(
          (resp: ServerModels.UserInfoViewModel) => {
            this.userInfo = resp;
            AccountComponent.userInfo = resp;
            this.onCancel();
          },
          (err: any) => this.messageService.add({ key: 'userInfoMsg', severity: 'error', summary: 'Error', detail: err.Message || err.error?.ExceptionMessage || errorMessage })
        )
      }
      else {
        this.showUserCommentsForm = true;
      }
    }
  }

  private updateAgency(isSelected: boolean): void {
    this._showAgency = isSelected;
    if (isSelected) {
      this.userProfileForm.get('AgencyID').setValidators(Validators.required);
      this.userProfileForm.get('StateID').setValidators(Validators.required);
    } else {
      this.userProfileForm.get('AgencyID').clearValidators();
      this.userProfileForm.get('StateID').clearValidators();
    }

    this.userProfileForm.get('StateID').reset('');
    this.userProfileForm.get('AgencyID').reset({ value: '', disabled: true });
  }

  private updateFieldDivision(isSelected: boolean): void {
    this._showFieldDivision = isSelected;
    if (isSelected) {
      this.userProfileForm.get('FieldDivisionID').setValidators(Validators.required);
      this.userProfileForm.get('FieldDivisionGroupID').setValidators(Validators.required);
    } else {
      this.userProfileForm.get('FieldDivisionID').clearValidators();
      this.userProfileForm.get('FieldDivisionGroupID').clearValidators();
    }

    this.userProfileForm.get('FieldDivisionID').reset('');
    this.userProfileForm.get('FieldDivisionGroupID').reset({ value: '', disabled: true });
  }

  private updateRoles(canSelectPowerUser: boolean): void {
    if (canSelectPowerUser) {
      this.filteredRoles = [...this.roles];
    } else {
      this.filteredRoles = [...this.roles.filter(r => r.Name !== 'Power User')];
    }
    (<FormArray>this.userProfileForm.get('Roles')).at(0).get('Id').reset('');
  }

  private updateJobTitles(userTypeId: string) {
    const jobTitleCtrl = this.userProfileForm.get('JobTitleID');
    if (!!userTypeId) {
      this.filteredJobTitles = this.jobTitles.filter(j => j.UserTypeID.toLowerCase() == userTypeId.toLowerCase());
    }
    this._showOtherJobTitle = false;
    jobTitleCtrl.reset({ value: '', disabled: !userTypeId });
  }

  private updateAgenciesCtrl(stateId: string, agencyId: string = '') {
    this.filteredAgencies = [];
    const agencyCtrl = this.userProfileForm.get('AgencyID');
    if (!!stateId) {
      this.agencyService.getAgenciesForState(stateId).pipe(take(1)).subscribe((agencies: ServerModels.ENESS_REF_Agency[]) => {
        this.filteredAgencies = agencies;
        agencyCtrl.reset({ value: agencyId, disabled: (this.isCDSApprover() && this.isNESSAdmin()) });
      });
    } else {
      agencyCtrl.reset({ value: '', disabled: true });
    }
  }

  private updateFDGroups(fieldDivisionId: string) {
    const fdGroupCtrl = this.userProfileForm.get('FieldDivisionGroupID');
    if (!!fieldDivisionId) {
      this.filteredFDGroup = this.fdGroups.filter(g => g.FieldDivisionID.toLowerCase() == fieldDivisionId.toLowerCase()) || [];
    }
    fdGroupCtrl.reset({ value: '', disabled: !fieldDivisionId });
  }

  public onUserHistorySubmitted(): void {
    this.messageService.clear();
    this._formData.UserName = this.userInfoForOtherUser.Email;
    this._formData.StatusNotes = this.userHistoryForm.get("Comments").value;
    this.adminService.updateUserByAdmin(this._formData).pipe(takeUntil(this._unsubscribe$)).subscribe(
      (resp: ServerModels.UserInfoViewModel) => {
        this.userInfoForOtherUser = resp;
        this.userUpdated.emit(true);
        this.onCancel();
      },
      (err: any) => {
        this.showUserCommentsForm = false;
        let errorMessage = 'An error occurred while updating the user profile. The user profile changes were not saved.';
        // TODO BLF - this probably doesnt work anymore
        if (err.error.InnerException && err.error.InnerException.ExceptionMessage == 'FieldDivisionLeadership') {
          errorMessage = err.error?.ExceptionMessage || errorMessage;
          this.isFieldDivisionLeadership = true;
        }
        this.messageService.add({ key: 'userInfoMsg', severity: 'error', summary: 'Error', detail: errorMessage });
      },
      () => {
        this.showUserCommentsForm = false
        this.userHistoryForm.reset({
          Comments: ''
        });
      }
    )
  }

  public onCloseUserHistoryForm(): void {
    this.userHistoryForm.reset({
      Comments: ''
    });
    this.showUserCommentsForm = false;
  }

  public onMessageClose(message: Message[]): void {
    if (this.route.snapshot.routeConfig.component.name === 'AdministrationComponent' && this.authService.hasRole(['NESS Administrator']) && this.isFieldDivisionLeadership) {
      this.isFieldDivisionLeadership = false;
      this.onChange.emit({ originalEvent: null, index: 3 });
    }
  }
}
